import { ActiveSubscriptionStatuses, PlatformUser, SubscriptionPlan } from 'models/user'
import { UserIsAuth0User, UserIsPlatformUserClient, UserIsPlatformUserCreative } from 'utils/typeguards'

import { Auth0User } from 'models/auth'
import { Userpilot } from 'userpilot'
import { getRolesFromUser } from 'utils/auth'

export const USERPILOT_ID = process.env.REACT_APP_USERPILOT_ID

/** Type used for Userpilot configuration */
export type TrackUserPilotInterface = {
  user_id?: string
  email?: string
  name?: string
  company?: TrackUserPilotCompany
  country: string
  locale_code: string
  roles?: string
  subscribedWorkspaceIDs?: string,
}

type TrackUserPilotCompany = {
  id: string,
}

/**
 * Convert language enum value to ISO 639-1 code for Userpilot localization
 * This enables Userpilot's localization feature to work with our language system
 * @param language - Language enum value (e.g., 'EN', 'FR', 'DE')
 * @returns ISO 639-1 language code (e.g., 'en', 'fr', 'de')
 */
export const getLocaleCode = (language: string): string => {
  const languageMap: Record<string, string> = {
    'EN': 'en',
    'FR': 'fr',
    'DE': 'de',
    'ES': 'es',
    'NL': 'nl',
    'IT': 'it',
  }

  return languageMap[language] || 'en'
}

// Initialize Userpilot
export const initUserpilot = () => {
  if (USERPILOT_ID) {
    Userpilot.initialize(USERPILOT_ID)
  }
}

export const trackUserpilot = (user: Auth0User | PlatformUser) => {
  if (!user) return

  const localeCode = getLocaleCode(user.language)

  const trackUser: TrackUserPilotInterface = {
    user_id: user.sfRecordId,
    email: user.email,
    name: user.name,
    country: user.country,
    locale_code: localeCode,
  }

  if (UserIsAuth0User(user)) {
    trackUser.roles = Array.from(
      getRolesFromUser(user).all.values()
    ).join(', ')
  } else {
    if (UserIsPlatformUserClient(user)) {
      const subscribedWorkspaces = user.workspaces.filter(workspace =>
        workspace.plan !== SubscriptionPlan.PERSONAL && ActiveSubscriptionStatuses.has(workspace.status)
      )

      trackUser.subscribedWorkspaceIDs = subscribedWorkspaces.map(workspace => workspace.id).join(',')

      if (user.organizationSfId) {
        trackUser.company = {
          id: user.organizationSfId
        }
      }
    } else if (UserIsPlatformUserCreative(user)) {
      if (user.organizationSfId) {
        trackUser.company = {
          id: user.organizationSfId
        }
      }
    }
  }

  Userpilot.identify(
    user.sfRecordId,
    trackUser
  )
}

/** Destroy Userpilot session and clear authentication/cache */
export const destroyUserpilot = () => {
  Userpilot.destroy()
}
